import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { Loader } from 'lucide-react';

const CatalogSearch = ({ value, onSelect, onChange, placeholder, onBlur }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync when parent resets the value externally
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Click outside closes dropdown
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (term) => {
    if (term.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const { data } = await supabase
        .from('customs_catalog')
        .select('id, descripcion, unidad_medida, unit_type, fob_usd, cip_usd, categoria')
        .ilike('descripcion', '%' + term + '%')
        .order('descripcion', { ascending: true })
        .limit(12);
      setResults(data || []);
      setOpen(true);
      setHighlighted(-1);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (onChange) onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (item) => {
    setQuery(item.descripcion);
    setOpen(false);
    setResults([]);
    if (onSelect) onSelect(item);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && highlighted >= 0) {
      e.preventDefault();
      handleSelect(results[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Wrap matched portion in <strong>
  const highlight = (text, term) => {
    if (!term || term.length < 2) return text;
    const idx = text.toUpperCase().indexOf(term.toUpperCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <strong style={{ fontWeight: 700, color: 'var(--amber)' }}>{text.slice(idx, idx + term.length)}</strong>
        {text.slice(idx + term.length)}
      </>
    );
  };

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      <div style={{ position: 'relative' }}>
        <input
          className="input"
          style={{ padding: '6px', minHeight: focused || open ? '44px' : undefined }}
          placeholder={placeholder || 'Descripción del artículo'}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { setFocused(true); if (results.length > 0) setOpen(true); }}
          onBlur={() => { setFocused(false); if (onBlur) onBlur(); }}
          autoComplete="off"
        />
        {loading && (
          <span style={{ position: 'absolute', right: 8, top: 8, color: 'var(--text-tertiary)' }}>
            <Loader size={13} className="animate-spin" />
          </span>
        )}
      </div>

      {open && (
        <div className="tile" style={{
          position: 'absolute', zIndex: 50,
          padding: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '288px', overflowY: 'auto',
          width: '100%', top: '100%', left: 0, marginTop: '4px',
        }}>
          {results.length === 0 ? (
            <div className="empty-state" style={{ padding: '12px 16px' }}>
              No encontrado — valor libre
            </div>
          ) : results.map((item, idx) => (
            <div
              key={item.id}
              onMouseDown={() => handleSelect(item)}
              onMouseEnter={() => setHighlighted(idx)}
              className="row-item"
              style={{
                padding: '12px 16px', fontSize: '14px', cursor: 'pointer',
                borderBottom: '1px solid var(--border)',
                background: idx === highlighted ? 'var(--bg)' : 'transparent',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'var(--text-main)', fontWeight: 500 }}>
                  {highlight(item.descripcion, query)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  {item.categoria && <span>{item.categoria}</span>}
                  {item.fob_usd != null && (
                    <span style={{ marginLeft: item.categoria ? '6px' : 0, color: 'var(--text-tertiary)' }}>
                      · ${parseFloat(item.fob_usd).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogSearch;
